"use client";

import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";

/**
 * STORY-1302 — site-wide "BETA — Virtual Money" banner.
 *
 * Sits above SiteHeader on every page. Closed beta runs on virtual
 * cash per ADR-014; this is the visible reminder users see at all
 * times so there's no ambiguity about whether they're trading real
 * funds. Removed (or downgraded) when EPIC-014's validation gate
 * passes and real-money infrastructure activates.
 *
 * 2026-05-25 — styling copied 1:1 from components/v2/BetaBanner.tsx:
 *   - forest substrate (#2d3b2e), cream text (#F1ECE2)
 *   - 11px mono font, weight 500, letter-spacing 0.05em
 *   - ⚠ unicode triangle (not the Lucide FlaskConical icon)
 *   - 8px / 24px padding, no border
 *   - centered
 * Founder direction: copy the v2 colors AND typography exactly.
 */
export function BetaBanner() {
  const { t } = useTranslation();
  const pathname = usePathname();
  // STORY-616 — embed routes are iframe-only; hide chrome.
  if (pathname?.startsWith("/embed")) return null;
  // v2 redesign (2026-05-21) — the v2 surface ships its own forest-
  // green BetaBanner from components/v2/BetaBanner.tsx; suppress the
  // v1 banner so it doesn't stack on every /v2/* page.
  if (pathname?.startsWith("/v2")) return null;
  if (pathname?.startsWith("/v3")) return null;
  if (pathname?.startsWith("/v4")) return null;
  if (pathname?.startsWith("/v5")) return null;
  // /v6 used to suppress here when it was a demo route. With v6
  // promoted to localhost main theme 2026-05-24, /v6 inherits this
  // banner like every other route.
  return (
    <div
      role="region"
      aria-label="Beta status disclosure"
      style={{
        width: "100%",
        background: "#2d3b2e",
        color: "#F1ECE2",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        textAlign: "center",
        padding: "8px 24px",
        lineHeight: 1.4,
      }}
    >
      <span aria-hidden="true" style={{ marginRight: 6 }}>
        ⚠
      </span>
      {t("betaBanner.badge")} · {t("betaBanner.body")}
    </div>
  );
}
